import { useCallback, useEffect, useRef, useState } from 'react'
import {
  Container,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Alert,
  CircularProgress,
  LinearProgress,
  Snackbar,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  TablePagination,
  Chip,
  Paper,
  IconButton,
  Stack,
  Grid,
  Tooltip,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  InputAdornment,
} from '@mui/material'
import SyncIcon from '@mui/icons-material/Sync'
import RefreshIcon from '@mui/icons-material/Refresh'
import DownloadIcon from '@mui/icons-material/Download'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import CloseIcon from '@mui/icons-material/Close'
import SearchIcon from '@mui/icons-material/Search'
import { stockSyncAdminService } from '../services'
import useAuthStore from '../store/authStore'
import { hasAdminPermission, PERMISSION_SUBJECTS } from '../utils/adminPermissions'
import ReportTablePagination from '../components/SalesReport/ReportTable/ReportTablePagination'
import { DEFAULT_ROWS, ORG_SEARCH_DEBOUNCE_MS } from '../components/SalesReport/constants'
import { DASHBOARD_COLORS } from '../components/Dashboard/dashboardFormatters'

const parseLocationCodesInput = (raw) => {
  const parts = String(raw || '')
    .split(/[\s,]+/)
    .map((part) => part.trim())
    .filter(Boolean)
  const seen = new Set()
  const out = []
  for (const code of parts) {
    if (seen.has(code)) continue
    seen.add(code)
    out.push(code)
  }
  return out
}

const MANUAL_SYNC_TIME_MIN = 90
const MANUAL_SYNC_TIME_MAX = 10080
const POLL_INTERVAL_MS = 5000
const STUCK_AFTER_MS = 3 * 60 * 1000
const DRAWER_ROWS_OPTIONS = [20, 50, 100]

const detailTableHeadSx = {
  fontSize: '10.5px',
  fontWeight: 600,
  color: '#888882',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  borderBottom: `1px solid ${DASHBOARD_COLORS.border}`,
  py: 0,
  pb: 1.25,
  whiteSpace: 'nowrap',
}

const detailTableCellSx = {
  fontSize: '13px',
  borderBottom: `1px solid ${DASHBOARD_COLORS.border}`,
  py: 1.125,
  px: 1,
}

const TILE_CONFIG = {
  processed: {
    label: 'Processed',
    itemType: 'location_processed',
    searchable: true,
    countKey: 'locations_processed',
  },
  failed: {
    label: 'Failed locations',
    itemType: 'location_failed',
    searchable: true,
    countKey: 'locations_failed',
  },
  updated: {
    label: 'Updated',
    itemType: 'updated',
    searchable: false,
    countKey: 'rows_updated',
  },
  inserted: {
    label: 'Inserted',
    itemType: 'inserted',
    searchable: false,
    countKey: 'rows_inserted',
  },
  skipped: {
    label: 'Skipped',
    itemType: 'skipped',
    searchable: false,
    countKey: 'rows_skipped',
  },
}

const ARTIFACT_KIND = {
  PRICE_CHANGES: 'price-changes',
  API_DATA: 'api-data',
}

const artifactKeyField = {
  [ARTIFACT_KIND.PRICE_CHANGES]: 'price_change_csv_s3_key',
  [ARTIFACT_KIND.API_DATA]: 'api_data_csv_s3_key',
}

const artifactLabel = {
  [ARTIFACT_KIND.PRICE_CHANGES]: 'Price Changes',
  [ARTIFACT_KIND.API_DATA]: 'API Data',
}

const headCellSx = {
  fontWeight: 600,
  color: '#1a1a1a',
  whiteSpace: 'nowrap',
}

const formatDate = (iso) => {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

const formatCount = (value) => (Number(value) || 0).toLocaleString()

const csvEscape = (value) => {
  const text = value == null ? '' : String(value)
  if (/[",\n\r]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`
  }
  return text
}

const downloadTextFile = (filename, text, mime = 'text/csv;charset=utf-8') => {
  const blob = new Blob([text], { type: mime })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

/** Admin-facing labels for Redis retry / rate-limit metrics. */
const COL = {
  rateLimitHits: 'Rate limit hits',
  rateLimitHitsHint: 'Times the stock API returned HTTP 429 (too many requests) for this location',
  requestTimeouts: 'Request timeouts',
  requestTimeoutsHint: 'Times the stock API request timed out for this location',
  retryPasses: 'Retry passes',
  retryPassesHint: 'How many failed-location retry passes included this location',
  failureReasons: 'Failure reasons',
  failureReasonsHint: 'What went wrong for this location during the run (hover for technical detail)',
}

const REASON_PART_RE = /^(.+)\((\d+)\)$/

/**
 * Map worker/technical failure strings to admin-readable labels.
 * Keep raw text available via tooltip for engineering.
 */
const describeFailureReason = (raw) => {
  const reason = String(raw || '').trim()
  if (!reason) return { label: '—', detail: '' }

  if (reason === 'HTTP 429') {
    return {
      label: 'API rate-limited (temporary — retried)',
      detail: reason,
    }
  }
  if (reason === 'HTTP 429 from stock API') {
    return {
      label: 'API rate limit — failed after retries',
      detail: reason,
    }
  }
  const httpFromApi = reason.match(/^HTTP (\d+) from stock API$/)
  if (httpFromApi) {
    const code = httpFromApi[1]
    return {
      label: `Stock API error (HTTP ${code})`,
      detail: reason,
    }
  }
  if (reason.startsWith('Stock API timeout:')) {
    return {
      label: 'Stock API request timed out',
      detail: reason,
    }
  }
  if (reason.startsWith('Stock API connection error:')) {
    return {
      label: 'Could not reach stock API',
      detail: reason,
    }
  }
  if (reason.startsWith('Stock API request failed:')) {
    return {
      label: 'Stock API request failed',
      detail: reason,
    }
  }
  if (reason === 'Stock API invalid JSON') {
    return {
      label: 'Stock API returned invalid data',
      detail: reason,
    }
  }
  if (reason === 'Stock API unexpected response payload') {
    return {
      label: 'Stock API returned unexpected data',
      detail: reason,
    }
  }
  if (reason === 'Failed while applying stock batch') {
    return {
      label: 'Failed while saving inventory',
      detail: reason,
    }
  }
  if (reason === 'Stopped by admin') {
    return { label: 'Stopped by admin', detail: reason }
  }
  if (reason === 'Failed after retries') {
    return { label: 'Failed after retries', detail: reason }
  }
  return { label: reason, detail: reason }
}

/** Format tally string like "HTTP 429(3); HTTP 429 from stock API(1)". */
const formatReasonsSeenForAdmin = (raw) => {
  const text = String(raw || '').trim()
  if (!text) return { label: '—', detail: '' }
  const parts = text.split(';').map((p) => p.trim()).filter(Boolean)
  const labels = []
  for (const part of parts) {
    const match = part.match(REASON_PART_RE)
    if (match) {
      const described = describeFailureReason(match[1].trim())
      const count = match[2]
      labels.push(`${described.label} ×${count}`)
    } else {
      labels.push(describeFailureReason(part).label)
    }
  }
  return {
    label: labels.join('; '),
    detail: text,
  }
}

const formatFailureReasonForAdmin = (raw) => describeFailureReason(raw)

const buildDrawerItemsCsv = (itemType, items) => {
  const rows = items || []
  if (itemType === 'location_processed') {
    const lines = [
      [
        'location_code',
        'location_id',
        'location_name',
        'rows_updated',
        'rows_inserted',
        'rows_skipped',
        'rate_limit_hits',
        'request_timeouts',
        'retry_passes',
        'completed_at',
      ].join(','),
    ]
    for (const item of rows) {
      lines.push(
        [
          csvEscape(item.location_code),
          csvEscape(item.location_id),
          csvEscape(item.location_name),
          csvEscape(item.rows_updated ?? 0),
          csvEscape(item.rows_inserted ?? 0),
          csvEscape(item.rows_skipped ?? 0),
          csvEscape(item.http_429_count ?? 0),
          csvEscape(item.timeout_count ?? 0),
          csvEscape(item.retry_pass_count ?? 0),
          csvEscape(item.updated_at || item.created_at || ''),
        ].join(','),
      )
    }
    return `${lines.join('\n')}\n`
  }
  if (itemType === 'location_failed') {
    const lines = [
      [
        'location_code',
        'location_id',
        'location_name',
        'failure_reason',
        'failure_reason_raw',
        'rate_limit_hits',
        'request_timeouts',
        'retry_passes',
        'failure_reasons',
        'failure_reasons_raw',
        'failed_at',
      ].join(','),
    ]
    for (const item of rows) {
      lines.push(
        [
          csvEscape(item.location_code),
          csvEscape(item.location_id),
          csvEscape(item.location_name),
          csvEscape(
            formatFailureReasonForAdmin(
              item.failure_reason || item.last_reason,
            ).label,
          ),
          csvEscape(item.failure_reason || item.last_reason),
          csvEscape(item.http_429_count ?? 0),
          csvEscape(item.timeout_count ?? 0),
          csvEscape(item.retry_pass_count ?? 0),
          csvEscape(formatReasonsSeenForAdmin(item.reasons_seen).label),
          csvEscape(item.reasons_seen),
          csvEscape(item.last_failed_at || item.updated_at || item.created_at || ''),
        ].join(','),
      )
    }
    return `${lines.join('\n')}\n`
  }
  if (itemType === 'updated') {
    const lines = [
      [
        'location_code',
        'sku',
        'product_name',
        'quantity',
        'unit_cost_before',
        'unit_cost_after',
        'unit_price_before',
        'unit_price_after',
      ].join(','),
    ]
    for (const item of rows) {
      lines.push(
        [
          csvEscape(item.location_code),
          csvEscape(item.sku),
          csvEscape(item.product_name),
          csvEscape(item.quantity),
          csvEscape(item.unit_cost_before),
          csvEscape(item.unit_cost_after),
          csvEscape(item.unit_price_before),
          csvEscape(item.unit_price_after),
        ].join(','),
      )
    }
    return `${lines.join('\n')}\n`
  }
  if (itemType === 'inserted') {
    const lines = [
      'location_code,sku,product_name,quantity,unit_cost_after,unit_price_after',
    ]
    for (const item of rows) {
      lines.push(
        [
          csvEscape(item.location_code),
          csvEscape(item.sku),
          csvEscape(item.product_name),
          csvEscape(item.quantity),
          csvEscape(item.unit_cost_after),
          csvEscape(item.unit_price_after),
        ].join(','),
      )
    }
    return `${lines.join('\n')}\n`
  }
  if (itemType === 'skipped') {
    const lines = ['location_code,sku,product_name,quantity,skip_reason']
    for (const item of rows) {
      lines.push(
        [
          csvEscape(item.location_code),
          csvEscape(item.sku),
          csvEscape(item.product_name),
          csvEscape(item.quantity),
          csvEscape(item.skip_reason),
        ].join(','),
      )
    }
    return `${lines.join('\n')}\n`
  }
  return 'message\nUnsupported report type\n'
}

const AdminReasonText = ({ raw, tally = false }) => {
  const formatted = tally
    ? formatReasonsSeenForAdmin(raw)
    : formatFailureReasonForAdmin(raw)
  if (!raw || formatted.label === '—') {
    return '—'
  }
  const tip =
    formatted.detail && formatted.detail !== formatted.label
      ? `Technical detail: ${formatted.detail}`
      : formatted.detail || formatted.label
  return (
    <Tooltip title={tip}>
      <Box
        component="span"
        sx={{
          cursor: 'help',
          display: '-webkit-box',
          WebkitLineClamp: 3,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}
      >
        {formatted.label}
      </Box>
    </Tooltip>
  )
}

const formatDuration = (startedAt, finishedAt) => {
  if (!startedAt) return '—'
  const from = new Date(startedAt).getTime()
  const to = finishedAt ? new Date(finishedAt).getTime() : Date.now()
  if (Number.isNaN(from) || Number.isNaN(to) || to < from) return '—'
  const totalSec = Math.floor((to - from) / 1000)
  const hours = Math.floor(totalSec / 3600)
  const minutes = Math.floor((totalSec % 3600) / 60)
  const seconds = totalSec % 60
  if (hours > 0) return `${hours}h ${minutes}m`
  if (minutes > 0) return `${minutes}m ${seconds}s`
  return `${seconds}s`
}

const statusColor = (run) => {
  if (!run) return 'default'
  if (run.status === 'running') return 'info'
  if (run.status === 'cancelled') return 'default'
  if (run.status === 'failed') return 'error'
  if (run.status === 'completed' && (run.locations_failed || 0) > 0) return 'warning'
  if (run.status === 'completed') return 'success'
  return 'default'
}

const statusLabel = (run) => {
  if (!run) return 'None'
  if (run.status === 'completed' && (run.locations_failed || 0) > 0) {
    return 'Completed (partial)'
  }
  if (run.status === 'running' && run.cancel_requested_at) return 'Stopping…'
  if (run.status === 'running') return 'Running'
  if (run.status === 'cancelled') return 'Cancelled'
  if (run.status === 'failed') return 'Failed'
  if (run.status === 'completed') return 'Completed'
  return run.status
}

const triggerLabel = (run) => {
  if (!run) return '—'
  if (run.trigger === 'cron') return 'Cron'
  if (run.trigger === 'manual') return 'Manual'
  return run.trigger || '—'
}

const triggeredByLabel = (run) => {
  if (!run) return '—'
  if (run.triggered_by_email) return run.triggered_by_email
  if (run.trigger === 'cron') return 'cron'
  return '—'
}

const artifactUnavailableKey = (runId, kind) => `${runId}:${kind}`

const csvUnavailableReason = (run, kind) => {
  if (!run) return 'No run to download'
  if (run.status === 'running') return 'CSV is available after the run finishes'
  if (!run[artifactKeyField[kind]]) return 'CSV expires after 7 days'
  return 'CSV is no longer available'
}

const StatTile = ({ label, value, emphasize, onClick, clickable }) => (
  <Paper
    variant="outlined"
    onClick={clickable ? onClick : undefined}
    sx={{
      px: 2,
      py: 1.75,
      height: '100%',
      borderRadius: 2,
      bgcolor: emphasize ? 'warning.light' : 'grey.50',
      borderColor: emphasize ? 'warning.main' : 'divider',
      cursor: clickable ? 'pointer' : 'default',
      transition: 'box-shadow 0.15s ease',
      '&:hover': clickable
        ? { boxShadow: '0 2px 8px rgba(0,0,0,0.12)', borderColor: 'primary.main' }
        : undefined,
    }}
  >
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ display: 'block', mb: 0.5, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}
    >
      {label}
    </Typography>
    <Typography variant="h5" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', lineHeight: 1.2 }}>
      {formatCount(value)}
    </Typography>
  </Paper>
)

const formatMoney = (value) => {
  if (value == null || value === '') return '—'
  const n = Number(value)
  if (Number.isNaN(n)) return String(value)
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

const MetaItem = ({ label, value }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ display: 'block', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}
    >
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: 500, wordBreak: 'break-word' }}>
      {value}
    </Typography>
  </Box>
)

const canDownloadRun = (run, kind, unavailableIds) =>
  !!run?.[artifactKeyField[kind]] &&
  run.status !== 'running' &&
  !unavailableIds[artifactUnavailableKey(run.id, kind)]

const CsvDownloadButton = ({
  run,
  kind,
  unavailableIds,
  downloadingKey,
  onDownload,
  variant = 'outlined',
  compact = false,
}) => {
  const unavailableKey = artifactUnavailableKey(run?.id, kind)
  const hasKey = !!run?.[artifactKeyField[kind]]
  if (compact && (!run || run.status === 'running' || (!hasKey && !unavailableIds[unavailableKey]))) {
    return (
      <Typography component="span" variant="body2" color="text.secondary">
        —
      </Typography>
    )
  }
  const downloadable = canDownloadRun(run, kind, unavailableIds)
  const loading = downloadingKey === unavailableKey
  const label = artifactLabel[kind]
  const title = downloadable
    ? `${label} CSV is kept for 7 days`
    : csvUnavailableReason(run, kind)
  const button = compact ? (
    <span>
      <IconButton
        size="small"
        aria-label={downloadable ? `Download ${label}` : title}
        disabled={!downloadable || loading}
        onClick={() => onDownload(run.id, kind)}
      >
        {loading ? <CircularProgress size={16} /> : <DownloadIcon fontSize="small" />}
      </IconButton>
    </span>
  ) : (
    <span>
      <Button
        variant={downloadable ? variant : 'outlined'}
        size="small"
        startIcon={loading ? <CircularProgress size={16} /> : <DownloadIcon />}
        disabled={!run || !downloadable || loading}
        onClick={() => onDownload(run.id, kind)}
      >
        {`Download ${label}`}
      </Button>
    </span>
  )
  return <Tooltip title={title}>{button}</Tooltip>
}

const StockSyncAdminPage = () => {
  const adminPermissions = useAuthStore((s) => s.adminPermissions)
  const canWrite = hasAdminPermission(
    adminPermissions,
    PERMISSION_SUBJECTS.STOCK_SYNC,
    'write',
  )
  const [starting, setStarting] = useState(false)
  const [awaitingNewRun, setAwaitingNewRun] = useState(false)
  const awaitingNewRunSinceRef = useRef(null)
  /** Latest run id when "Starting…" began; clear when a different run appears. */
  const awaitingBaselineRunIdRef = useRef(null)
  const [statusLoading, setStatusLoading] = useState(true)
  const [historyLoading, setHistoryLoading] = useState(true)
  const [error, setError] = useState(null)
  const [latest, setLatest] = useState(null)
  const [runs, setRuns] = useState([])
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [total, setTotal] = useState(0)
  const [downloadingKey, setDownloadingKey] = useState(null)
  const [unavailableIds, setUnavailableIds] = useState({})
  const [syncTime, setSyncTime] = useState('')
  const [locationCodesInput, setLocationCodesInput] = useState('')
  const [snackbar, setSnackbar] = useState({ open: false, severity: 'success', message: '' })
  const [nowTick, setNowTick] = useState(() => Date.now())
  const [stopping, setStopping] = useState(false)
  const [drawer, setDrawer] = useState(null)
  const [drawerItems, setDrawerItems] = useState([])
  const [drawerLoading, setDrawerLoading] = useState(false)
  const [drawerError, setDrawerError] = useState(null)
  const [drawerPage, setDrawerPage] = useState(1)
  const [drawerRowsPerPage, setDrawerRowsPerPage] = useState(DEFAULT_ROWS)
  const [drawerTotal, setDrawerTotal] = useState(0)
  const [drawerSearch, setDrawerSearch] = useState('')
  const [drawerSearchInput, setDrawerSearchInput] = useState('')
  const [drawerDownloading, setDrawerDownloading] = useState(false)
  const [historyLocations, setHistoryLocations] = useState(null)
  const [historyLocationItems, setHistoryLocationItems] = useState([])
  const [historyLocationsLoading, setHistoryLocationsLoading] = useState(false)
  const [historyLocationsError, setHistoryLocationsError] = useState(null)
  const [historyLocationsPage, setHistoryLocationsPage] = useState(1)
  const [historyLocationsRows, setHistoryLocationsRows] = useState(DEFAULT_ROWS)
  const [historyLocationsTotal, setHistoryLocationsTotal] = useState(0)
  const [historyLocationsSearch, setHistoryLocationsSearch] = useState('')
  const [historyLocationsSearchInput, setHistoryLocationsSearchInput] = useState('')
  const [historyLocationsDownloading, setHistoryLocationsDownloading] = useState(false)

  const loadLatest = useCallback(async ({ quiet } = {}) => {
    if (!quiet) setStatusLoading(true)
    try {
      const latestRun = await stockSyncAdminService.getLatestRun()
      setLatest(latestRun || null)
    } catch (err) {
      if (!quiet) {
        const message =
          err.response?.data?.message ||
          err.response?.data?.error ||
          'Failed to load latest stock sync'
        setError(typeof message === 'string' ? message : 'Failed to load latest stock sync')
      }
    } finally {
      if (!quiet) setStatusLoading(false)
    }
  }, [])

  const loadHistory = useCallback(async (pageOverride, limitOverride) => {
    const nextPage = pageOverride ?? page
    const nextLimit = limitOverride ?? rowsPerPage
    setHistoryLoading(true)
    try {
      const list = await stockSyncAdminService.listRuns({
        page: nextPage + 1,
        limit: nextLimit,
      })
      setRuns(list?.items || [])
      setTotal(list?.pagination?.total || 0)
    } catch (err) {
      const message =
        err.response?.data?.message ||
        err.response?.data?.error ||
        'Failed to load stock sync history'
      setError(typeof message === 'string' ? message : 'Failed to load stock sync history')
    } finally {
      setHistoryLoading(false)
    }
  }, [page, rowsPerPage])

  const refresh = useCallback(async () => {
    setError(null)
    await Promise.all([loadLatest(), loadHistory()])
  }, [loadLatest, loadHistory])

  const loadDrawerItems = useCallback(async () => {
    if (!drawer?.runId || !drawer?.itemType) return
    setDrawerLoading(true)
    setDrawerError(null)
    try {
      const params = {
        item_type: drawer.itemType,
        page: drawerPage,
        limit: drawerRowsPerPage,
      }
      if (drawer.searchable && drawerSearch.trim()) {
        params.q = drawerSearch.trim()
      }
      const data = await stockSyncAdminService.listItems(drawer.runId, params)
      setDrawerItems(data?.items || [])
      setDrawerTotal(data?.pagination?.total || 0)
    } catch (err) {
      const message =
        err.response?.data?.message ||
        err.response?.data?.error ||
        'Failed to load items'
      setDrawerError(typeof message === 'string' ? message : 'Failed to load items')
      setDrawerItems([])
      setDrawerTotal(0)
    } finally {
      setDrawerLoading(false)
    }
  }, [drawer, drawerPage, drawerRowsPerPage, drawerSearch])

  const loadHistoryLocations = useCallback(async () => {
    if (!historyLocations?.runId || !historyLocations?.kind) return
    setHistoryLocationsLoading(true)
    setHistoryLocationsError(null)
    try {
      const params = {
        page: historyLocationsPage,
        limit: historyLocationsRows,
      }
      if (historyLocationsSearch.trim()) {
        params.q = historyLocationsSearch.trim()
      }
      let data
      if (historyLocations.kind === 'processed') {
        data = await stockSyncAdminService.listProcessedLocations(
          historyLocations.runId,
          params,
        )
      } else {
        data = await stockSyncAdminService.listFailedLocations(
          historyLocations.runId,
          params,
        )
      }
      setHistoryLocationItems(data?.items || [])
      setHistoryLocationsTotal(data?.pagination?.total || 0)
    } catch (err) {
      const message =
        err.response?.data?.message ||
        err.response?.data?.error ||
        'Failed to load locations'
      setHistoryLocationsError(
        typeof message === 'string' ? message : 'Failed to load locations',
      )
      setHistoryLocationItems([])
      setHistoryLocationsTotal(0)
    } finally {
      setHistoryLocationsLoading(false)
    }
  }, [
    historyLocations,
    historyLocationsPage,
    historyLocationsRows,
    historyLocationsSearch,
  ])

  useEffect(() => {
    if (!historyLocations) return
    void loadHistoryLocations()
  }, [historyLocations, loadHistoryLocations])

  useEffect(() => {
    if (!historyLocations) return undefined
    const timer = setTimeout(() => {
      if (historyLocationsSearchInput === historyLocationsSearch) return
      setHistoryLocationsPage(1)
      setHistoryLocationsSearch(historyLocationsSearchInput)
    }, ORG_SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [historyLocationsSearchInput, historyLocationsSearch, historyLocations])

  const openHistoryLocations = (run, kind) => {
    if (!run?.id) return
    const expectedCount =
      kind === 'processed'
        ? Number(run.locations_processed) || 0
        : Number(run.locations_failed) || (run.failed_location_codes || []).length
    if (expectedCount <= 0) return
    const codes =
      kind === 'processed'
        ? run.processed_location_codes || []
        : run.failed_location_codes || []
    setHistoryLocationsSearch('')
    setHistoryLocationsSearchInput('')
    setHistoryLocationsPage(1)
    setHistoryLocationsRows(DEFAULT_ROWS)
    setHistoryLocations({
      runId: run.id,
      kind,
      label: kind === 'processed' ? 'Processed locations' : 'Failed locations',
      expectedCount,
      codes: Array.isArray(codes) ? codes.filter(Boolean) : [],
    })
  }

  const closeHistoryLocations = () => {
    setHistoryLocations(null)
    setHistoryLocationItems([])
    setHistoryLocationsError(null)
    setHistoryLocationsDownloading(false)
  }

  const copyLocationCodes = async (codes, { successMessage } = {}) => {
    const list = (codes || []).filter((code) => typeof code === 'string' && code.trim())
    if (list.length === 0) {
      setSnackbar({
        open: true,
        severity: 'warning',
        message: 'No location codes to copy',
      })
      return
    }
    const text = list.join(', ')
    try {
      await navigator.clipboard.writeText(text)
      setSnackbar({
        open: true,
        severity: 'success',
        message:
          successMessage ||
          `Copied ${list.length} location code${list.length === 1 ? '' : 's'}`,
      })
    } catch {
      setSnackbar({
        open: true,
        severity: 'error',
        message: 'Failed to copy location codes',
      })
    }
  }

  const handleHistoryLocationsCsvDownload = async () => {
    if (!historyLocations?.runId || !historyLocations?.kind) return
    setHistoryLocationsDownloading(true)
    try {
      if (historyLocations.kind === 'processed') {
        await stockSyncAdminService.downloadProcessedLocationsCsv(
          historyLocations.runId,
        )
      } else {
        await stockSyncAdminService.downloadFailedLocationsCsv(
          historyLocations.runId,
        )
      }
    } catch (err) {
      const message =
        err.response?.data?.message ||
        err.response?.data?.error ||
        'Failed to download CSV'
      setSnackbar({
        open: true,
        severity: 'error',
        message: typeof message === 'string' ? message : 'Failed to download CSV',
      })
    } finally {
      setHistoryLocationsDownloading(false)
    }
  }

  useEffect(() => {
    loadLatest()
  }, [loadLatest])

  useEffect(() => {
    loadHistory()
  }, [loadHistory])

  // Always poll latest so cron-started runs appear without a full page refresh.
  // Poll faster while waiting for a just-triggered manual run to show up.
  useEffect(() => {
    const intervalMs = awaitingNewRun ? 1500 : POLL_INTERVAL_MS
    const id = setInterval(() => {
      setNowTick(Date.now())
      void loadLatest({ quiet: true })
    }, intervalMs)
    return () => clearInterval(id)
  }, [awaitingNewRun, loadLatest])

  // Stop the fast "awaiting new run" poll once the new run shows up (any
  // terminal/running status), or after a safety timeout. Fast-fail runs (e.g.
  // manage-api login / proxy down) often never appear as status=running.
  useEffect(() => {
    if (!awaitingNewRun) return undefined

    const clearAwaiting = () => {
      setAwaitingNewRun(false)
      awaitingNewRunSinceRef.current = null
      awaitingBaselineRunIdRef.current = null
    }

    const baselineId = awaitingBaselineRunIdRef.current
    const latestId = latest?.id ?? null
    const status = latest?.status
    const isNewRun = latestId != null && latestId !== baselineId
    const isTerminal =
      status === 'failed' || status === 'completed' || status === 'cancelled'

    if (status === 'running' || (isNewRun && isTerminal)) {
      clearAwaiting()
      void loadHistory(0)
      return undefined
    }

    const startedAt = awaitingNewRunSinceRef.current
    if (startedAt != null && nowTick - startedAt > 45000) {
      clearAwaiting()
    }
    return undefined
  }, [awaitingNewRun, latest?.status, latest?.id, loadHistory, nowTick])

  // Refresh history when a run starts or leaves running (complete / cancel / fail / stop).
  const prevLatestStatusRef = useRef(latest?.status)
  useEffect(() => {
    const prev = prevLatestStatusRef.current
    prevLatestStatusRef.current = latest?.status
    if (!latest?.status || prev === latest.status) return
    const started = latest.status === 'running' && prev !== 'running'
    const finished =
      prev === 'running' &&
      (latest.status === 'completed' ||
        latest.status === 'failed' ||
        latest.status === 'cancelled')
    if (started || finished) {
      void loadHistory(0)
    }
  }, [latest?.status, latest?.id, loadHistory])

  useEffect(() => {
    if (!drawer) return
    void loadDrawerItems()
  }, [drawer, loadDrawerItems])

  useEffect(() => {
    if (!drawer?.searchable) return undefined
    const timer = setTimeout(() => {
      if (drawerSearchInput === drawerSearch) return
      setDrawerPage(1)
      setDrawerSearch(drawerSearchInput)
    }, ORG_SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [drawerSearchInput, drawerSearch, drawer?.searchable])

  useEffect(() => {
    if (!drawer || latest?.status !== 'running') return undefined
    if (drawer.runId !== latest?.id) return undefined
    const id = setInterval(() => {
      void loadDrawerItems()
    }, POLL_INTERVAL_MS)
    return () => clearInterval(id)
  }, [drawer, latest?.status, latest?.id, loadDrawerItems])

  const openDrawer = (tileKey, runIdOverride) => {
    const runId = runIdOverride || latest?.id
    if (!runId) return
    const config = TILE_CONFIG[tileKey]
    if (!config) return
    if (!runIdOverride) {
      const count = Number(latest?.[config.countKey]) || 0
      if (count <= 0 && latest?.status !== 'running') return
    }
    setDrawerSearch('')
    setDrawerSearchInput('')
    setDrawerPage(1)
    setDrawerRowsPerPage(DEFAULT_ROWS)
    setDrawer({
      tileKey,
      label: config.label,
      itemType: config.itemType,
      searchable: config.searchable,
      runId,
    })
  }

  const historyCountForTile = (run, tileKey) => {
    if (!run) return 0
    if (tileKey === 'processed') return Number(run.locations_processed) || 0
    if (tileKey === 'failed') {
      return (
        Number(run.locations_failed) || (run.failed_location_codes || []).length || 0
      )
    }
    if (tileKey === 'updated') return Number(run.rows_updated) || 0
    if (tileKey === 'inserted') return Number(run.rows_inserted) || 0
    if (tileKey === 'skipped') return Number(run.rows_skipped) || 0
    return 0
  }

  const openHistoryDetail = async (run, tileKey) => {
    if (!run?.id) return
    const config = TILE_CONFIG[tileKey]
    if (!config) return
    const expected = historyCountForTile(run, tileKey)
    if (expected <= 0) return
    try {
      const data = await stockSyncAdminService.listItems(run.id, {
        item_type: config.itemType,
        page: 1,
        limit: 1,
      })
      if ((data?.pagination?.total || 0) > 0) {
        openDrawer(tileKey, run.id)
        return
      }
    } catch {
      // Fall through to PG fallback / message.
    }
    if (tileKey === 'processed' || tileKey === 'failed') {
      openHistoryLocations(run, tileKey)
      return
    }
    setSnackbar({
      open: true,
      severity: 'info',
      message:
        'Product-level detail for this run is no longer available (Redis window expired).',
    })
  }

  const closeDrawer = () => {
    setDrawer(null)
    setDrawerItems([])
    setDrawerError(null)
    setDrawerDownloading(false)
  }

  const handleDrawerCsvDownload = async () => {
    if (!drawer?.runId || !drawer?.itemType) return
    setDrawerDownloading(true)
    try {
      const pageSize = 100
      let page = 1
      let totalPages = 1
      const allItems = []
      while (page <= totalPages) {
        const params = {
          item_type: drawer.itemType,
          page,
          limit: pageSize,
        }
        if (drawer.searchable && drawerSearch.trim()) {
          params.q = drawerSearch.trim()
        }
        const data = await stockSyncAdminService.listItems(drawer.runId, params)
        allItems.push(...(data?.items || []))
        totalPages = Math.max(1, data?.pagination?.totalPages || 1)
        page += 1
        if (page > 500) break
      }
      const csv = buildDrawerItemsCsv(drawer.itemType, allItems)
      const suffix = drawer.tileKey || drawer.itemType || 'report'
      downloadTextFile(`stock-sync-${drawer.runId}-${suffix}.csv`, csv)
    } catch (err) {
      const message =
        err.response?.data?.message ||
        err.response?.data?.error ||
        'Failed to download CSV'
      setSnackbar({
        open: true,
        severity: 'error',
        message: typeof message === 'string' ? message : 'Failed to download CSV',
      })
    } finally {
      setDrawerDownloading(false)
    }
  }

  const handleRunSync = async () => {
    const trimmed = String(syncTime).trim()
    let payload = {}
    if (trimmed !== '') {
      const parsed = Number(trimmed)
      if (!Number.isInteger(parsed) || parsed < MANUAL_SYNC_TIME_MIN || parsed > MANUAL_SYNC_TIME_MAX) {
        setError(
          `Sync time must be an integer between ${MANUAL_SYNC_TIME_MIN} and ${MANUAL_SYNC_TIME_MAX}. Default is 90 (also the minimum). Leave empty to use 90.`,
        )
        return
      }
      payload = { sync_time: parsed }
    }
    const locationCodes = parseLocationCodesInput(locationCodesInput)
    if (locationCodes.length > 2000) {
      setError('At most 2000 location codes can be synced in one manual run.')
      return
    }
    if (locationCodes.length > 0) {
      payload = { ...payload, location_codes: locationCodes }
    }
    try {
      setStarting(true)
      setError(null)
      awaitingBaselineRunIdRef.current = latest?.id ?? null
      const data = await stockSyncAdminService.runStockSync(payload)
      setSnackbar({
        open: true,
        severity: 'success',
        message: data?.message || 'Stock sync started',
      })
      setPage(0)
      awaitingNewRunSinceRef.current = Date.now()
      setAwaitingNewRun(true)
      await Promise.all([loadLatest({ quiet: true }), loadHistory(0)])
    } catch (err) {
      const status = err.response?.status
      const message =
        err.response?.data?.message ||
        err.response?.data?.error ||
        'Failed to start stock sync'
      if (status === 409) {
        setSnackbar({ open: true, severity: 'warning', message })
        awaitingBaselineRunIdRef.current = latest?.id ?? null
        awaitingNewRunSinceRef.current = Date.now()
        setAwaitingNewRun(true)
        await Promise.all([loadLatest({ quiet: true }), loadHistory()])
      } else {
        setAwaitingNewRun(false)
        awaitingNewRunSinceRef.current = null
        awaitingBaselineRunIdRef.current = null
        setError(typeof message === 'string' ? message : 'Failed to start stock sync')
      }
    } finally {
      setStarting(false)
    }
  }

  const handleDownload = async (runId, kind) => {
    const fetchArtifact =
      kind === ARTIFACT_KIND.API_DATA
        ? stockSyncAdminService.getApiData
        : stockSyncAdminService.getPriceChanges
    const unavailableKey = artifactUnavailableKey(runId, kind)
    try {
      setDownloadingKey(unavailableKey)
      const data = await fetchArtifact(runId)
      if (!data?.download_url) {
        throw new Error('No download URL')
      }
      window.open(data.download_url, '_blank', 'noopener,noreferrer')
    } catch (err) {
      const status = err.response?.status
      const message =
        err.response?.data?.message ||
        err.response?.data?.error ||
        'Failed to download artifact'
      if (status === 404) {
        setUnavailableIds((prev) => ({ ...prev, [unavailableKey]: true }))
        setSnackbar({
          open: true,
          severity: 'warning',
          message: typeof message === 'string' ? message : 'Artifact is no longer available',
        })
      } else {
        setSnackbar({
          open: true,
          severity: 'error',
          message: typeof message === 'string' ? message : 'Failed to download artifact',
        })
      }
    } finally {
      setDownloadingKey(null)
    }
  }

  const latestRunning = latest?.status === 'running'
  const stopRequested = Boolean(latestRunning && latest?.cancel_requested_at)
  const showLatestLoadingBar = starting || awaitingNewRun || latestRunning
  const awaitingStatus = statusLoading || starting || awaitingNewRun
  const runDisabled = awaitingStatus || latestRunning
  const locationsTotal = Number(latest?.locations_total) || 0
  const locationsProcessed = Number(latest?.locations_processed) || 0
  const progressPct =
    locationsTotal > 0
      ? Math.min(100, Math.round((locationsProcessed / locationsTotal) * 100))
      : latestRunning
        ? 0
        : 100
  const lastProgressAt = latest?.last_progress_at
    ? new Date(latest.last_progress_at).getTime()
    : null
  const startedAtMs = latest?.started_at ? new Date(latest.started_at).getTime() : null
  const heartbeatMs =
    lastProgressAt != null && !Number.isNaN(lastProgressAt)
      ? lastProgressAt
      : startedAtMs
  const possiblyStuck =
    Boolean(latest?.possibly_stuck) ||
    (latestRunning &&
      heartbeatMs != null &&
      !Number.isNaN(heartbeatMs) &&
      nowTick - heartbeatMs > STUCK_AFTER_MS)
  const durationDisplay = formatDuration(latest?.started_at, latest?.finished_at)

  const handleStopRun = async () => {
    if (!latest?.id) return
    try {
      setStopping(true)
      setError(null)
      await stockSyncAdminService.stopRun(latest.id)
      setSnackbar({
        open: true,
        severity: 'success',
        message: possiblyStuck
          ? 'Orphaned sync marked cancelled. You can start a new sync.'
          : 'Stop requested. Sync will finish the current batch and then cancel.',
      })
      await Promise.all([loadLatest(), loadHistory()])
    } catch (err) {
      const message =
        err.response?.data?.message ||
        err.response?.data?.error ||
        'Failed to stop sync'
      setError(typeof message === 'string' ? message : 'Failed to stop sync')
    } finally {
      setStopping(false)
    }
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
        <SyncIcon color="primary" fontSize="large" />
        <Typography variant="h4" component="h1" sx={{ flexGrow: 1, fontWeight: 700 }}>
          Stock sync
        </Typography>
        <Button
          variant="outlined"
          startIcon={
            statusLoading || historyLoading ? <CircularProgress size={18} /> : <RefreshIcon />
          }
          onClick={refresh}
          disabled={statusLoading || historyLoading}
        >
          Refresh
        </Button>
      </Box>

      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Pull current stock from Shwapno manage-api into admin inventory for all locations.
        A full run can take a long time. Only one sync can run at a time (including the
        scheduled cron job). History is kept for 30 days; rich location/product detail
        is available for 14 days (Redis); CSV artifacts expire after 7 days.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            alignItems={{ sm: 'center' }}
            justifyContent="space-between"
          >
            <Box sx={{ flex: '1 1 auto' }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Manual sync
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {canWrite
                  ? 'The sync runs in the background after you start it.'
                  : 'You can view sync runs. Starting or stopping a sync requires write permission.'}
              </Typography>
            </Box>
            {canWrite && (
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              alignItems={{ sm: 'flex-start' }}
              flexWrap="wrap"
              useFlexGap
            >
              <TextField
                size="small"
                type="number"
                label="Sync time (this run only)"
                value={syncTime}
                onChange={(event) => setSyncTime(event.target.value)}
                inputProps={{
                  min: MANUAL_SYNC_TIME_MIN,
                  max: MANUAL_SYNC_TIME_MAX,
                  step: 1,
                }}
                helperText="Optional. Uses 90 if blank (min 90, max 10080)."
                sx={{ minWidth: 220 }}
                disabled={runDisabled}
              />
              <TextField
                size="small"
                label="Location codes (optional)"
                placeholder="F237, F239 or one per line"
                value={locationCodesInput}
                onChange={(event) => setLocationCodesInput(event.target.value)}
                helperText="Leave blank for all locations, or enter codes to sync only those."
                sx={{ minWidth: 280, flex: '1 1 280px' }}
                disabled={runDisabled}
                multiline
                minRows={1}
                maxRows={4}
              />
              <Button
                variant="contained"
                startIcon={
                  awaitingStatus ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <SyncIcon />
                  )
                }
                onClick={handleRunSync}
                disabled={runDisabled}
                sx={{ height: 40, whiteSpace: 'nowrap', mt: { sm: 0.5 } }}
              >
                {starting || awaitingNewRun
                  ? 'Starting…'
                  : latestRunning
                    ? 'Sync running…'
                    : statusLoading
                      ? 'Loading…'
                      : 'Run stock sync'}
              </Button>
            </Stack>
            )}
          </Stack>
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        {showLatestLoadingBar && <LinearProgress />}
        <CardContent>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            alignItems={{ sm: 'flex-start' }}
            justifyContent="space-between"
            sx={{ mb: 2.5 }}
          >
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                Latest run
              </Typography>
              {latest && (
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip
                    size="small"
                    variant="outlined"
                    label={statusLabel(latest)}
                    color={statusColor(latest)}
                  />
                  <Chip size="small" variant="outlined" label={triggerLabel(latest)} />
                </Stack>
              )}
            </Box>
            {latest && (
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {canWrite && latestRunning && (
                  <Button
                    variant="outlined"
                    color="warning"
                    disabled={stopping || (stopRequested && !possiblyStuck)}
                    onClick={handleStopRun}
                  >
                    {stopping
                      ? 'Stopping…'
                      : stopRequested && !possiblyStuck
                        ? 'Stopping…'
                        : stopRequested && possiblyStuck
                          ? 'Force stop'
                          : 'Stop sync'}
                  </Button>
                )}
                <CsvDownloadButton
                  run={latest}
                  kind={ARTIFACT_KIND.PRICE_CHANGES}
                  unavailableIds={unavailableIds}
                  downloadingKey={downloadingKey}
                  onDownload={handleDownload}
                  variant="contained"
                />
                <CsvDownloadButton
                  run={latest}
                  kind={ARTIFACT_KIND.API_DATA}
                  unavailableIds={unavailableIds}
                  downloadingKey={downloadingKey}
                  onDownload={handleDownload}
                  variant="contained"
                />
              </Stack>
            )}
          </Stack>

          {statusLoading && !latest ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={28} />
            </Box>
          ) : !latest ? (
            <Typography variant="body2" color="text.secondary">
              No sync runs recorded yet.
            </Typography>
          ) : (
            <Box>
              {possiblyStuck && !stopRequested && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Possibly stuck — no progress for over 3 minutes (worker may have
                  crashed). Use Stop sync to close the run and unlock a new sync.
                </Alert>
              )}
              {stopRequested && !possiblyStuck && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Stop requested — waiting for the worker to finish the current batch
                  and cancel.
                </Alert>
              )}
              {possiblyStuck && stopRequested && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  No progress for over 3 minutes after stop was requested. Click Force
                  stop to close the run and unlock a new sync.
                </Alert>
              )}
              <Grid container spacing={2} sx={{ mb: 2.5 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <MetaItem label="Triggered by" value={triggeredByLabel(latest)} />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <MetaItem label="Started" value={formatDate(latest.started_at)} />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <MetaItem
                    label="Finished"
                    value={latest.finished_at ? formatDate(latest.finished_at) : latestRunning ? 'In progress' : '—'}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <MetaItem label="Duration" value={durationDisplay} />
                </Grid>
              </Grid>

              {locationsTotal > 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  Progress: {formatCount(locationsProcessed)} / {formatCount(locationsTotal)}{' '}
                  locations
                  {latestRunning ? ` (${progressPct}%)` : ''}
                </Typography>
              )}

              <Grid container spacing={1.5}>
                <Grid item xs={6} sm={4} md>
                  <StatTile
                    label="Processed"
                    value={latest.locations_processed}
                    clickable={(Number(latest.locations_processed) || 0) > 0 || latestRunning}
                    onClick={() => openDrawer('processed')}
                  />
                </Grid>
                <Grid item xs={6} sm={4} md>
                  <StatTile
                    label="Failed locations"
                    value={latest.locations_failed}
                    clickable={(Number(latest.locations_failed) || 0) > 0 || latestRunning}
                    onClick={() => openDrawer('failed')}
                  />
                </Grid>
                <Grid item xs={6} sm={4} md>
                  <StatTile
                    label="Updated"
                    value={latest.rows_updated}
                    clickable={(Number(latest.rows_updated) || 0) > 0 || latestRunning}
                    onClick={() => openDrawer('updated')}
                  />
                </Grid>
                <Grid item xs={6} sm={4} md>
                  <StatTile
                    label="Inserted"
                    value={latest.rows_inserted}
                    clickable={(Number(latest.rows_inserted) || 0) > 0 || latestRunning}
                    onClick={() => openDrawer('inserted')}
                  />
                </Grid>
                <Grid item xs={6} sm={4} md>
                  <StatTile
                    label="Skipped"
                    value={latest.rows_skipped}
                    clickable={(Number(latest.rows_skipped) || 0) > 0 || latestRunning}
                    onClick={() => openDrawer('skipped')}
                  />
                </Grid>
              </Grid>

              {latest.error_summary && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {latest.error_summary}
                </Alert>
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
        <Box sx={{ px: 2, pt: 2, pb: 1.5 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            History (30 days)
          </Typography>
        </Box>
        {historyLoading && runs.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: 280,
              p: 3,
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: 1180 }}>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f8f9fb' }}>
                  <TableCell sx={headCellSx}>Status</TableCell>
                  <TableCell sx={headCellSx}>Trigger</TableCell>
                  <TableCell sx={headCellSx}>Triggered by</TableCell>
                  <TableCell sx={headCellSx}>Started</TableCell>
                  <TableCell sx={headCellSx}>Finished</TableCell>
                  <TableCell sx={headCellSx} align="right">
                    Processed
                  </TableCell>
                  <TableCell sx={headCellSx} align="right">
                    Failed
                  </TableCell>
                  <TableCell sx={headCellSx} align="right">
                    Updated
                  </TableCell>
                  <TableCell sx={headCellSx} align="right">
                    Inserted
                  </TableCell>
                  <TableCell sx={headCellSx} align="right">
                    Skipped
                  </TableCell>
                  <TableCell sx={headCellSx}>Failed locations</TableCell>
                  <TableCell sx={headCellSx} align="center">
                    Price Changes
                  </TableCell>
                  <TableCell sx={headCellSx} align="center">
                    API Data
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {runs.map((run) => {
                  const codes = run.failed_location_codes || []
                  return (
                    <TableRow
                      key={run.id}
                      hover
                      sx={{ '&:hover': { bgcolor: '#f8f9fb' } }}
                    >
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Chip
                          size="small"
                          variant="outlined"
                          label={statusLabel(run)}
                          color={statusColor(run)}
                        />
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{triggerLabel(run)}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{triggeredByLabel(run)}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(run.started_at)}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(run.finished_at)}</TableCell>
                      <TableCell align="right">
                        {(Number(run.locations_processed) || 0) > 0 ? (
                          <Typography
                            component="button"
                            type="button"
                            onClick={() => void openHistoryDetail(run, 'processed')}
                            sx={{
                              border: 'none',
                              bgcolor: 'transparent',
                              p: 0,
                              m: 0,
                              cursor: 'pointer',
                              textAlign: 'right',
                              font: 'inherit',
                              color: DASHBOARD_COLORS.blue,
                              textDecoration: 'underline',
                              '&:hover': { opacity: 0.85 },
                            }}
                          >
                            {formatCount(run.locations_processed)}
                          </Typography>
                        ) : (
                          formatCount(run.locations_processed)
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {(Number(run.locations_failed) || 0) > 0 ||
                        (run.failed_location_codes || []).length > 0 ? (
                          <Typography
                            component="button"
                            type="button"
                            onClick={() => void openHistoryDetail(run, 'failed')}
                            sx={{
                              border: 'none',
                              bgcolor: 'transparent',
                              p: 0,
                              m: 0,
                              cursor: 'pointer',
                              textAlign: 'right',
                              font: 'inherit',
                              color: DASHBOARD_COLORS.blue,
                              textDecoration: 'underline',
                              '&:hover': { opacity: 0.85 },
                            }}
                          >
                            {formatCount(run.locations_failed)}
                          </Typography>
                        ) : (
                          formatCount(run.locations_failed)
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {(Number(run.rows_updated) || 0) > 0 ? (
                          <Typography
                            component="button"
                            type="button"
                            onClick={() => void openHistoryDetail(run, 'updated')}
                            sx={{
                              border: 'none',
                              bgcolor: 'transparent',
                              p: 0,
                              m: 0,
                              cursor: 'pointer',
                              textAlign: 'right',
                              font: 'inherit',
                              color: DASHBOARD_COLORS.blue,
                              textDecoration: 'underline',
                              '&:hover': { opacity: 0.85 },
                            }}
                          >
                            {formatCount(run.rows_updated)}
                          </Typography>
                        ) : (
                          formatCount(run.rows_updated)
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {(Number(run.rows_inserted) || 0) > 0 ? (
                          <Typography
                            component="button"
                            type="button"
                            onClick={() => void openHistoryDetail(run, 'inserted')}
                            sx={{
                              border: 'none',
                              bgcolor: 'transparent',
                              p: 0,
                              m: 0,
                              cursor: 'pointer',
                              textAlign: 'right',
                              font: 'inherit',
                              color: DASHBOARD_COLORS.blue,
                              textDecoration: 'underline',
                              '&:hover': { opacity: 0.85 },
                            }}
                          >
                            {formatCount(run.rows_inserted)}
                          </Typography>
                        ) : (
                          formatCount(run.rows_inserted)
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {(Number(run.rows_skipped) || 0) > 0 ? (
                          <Typography
                            component="button"
                            type="button"
                            onClick={() => void openHistoryDetail(run, 'skipped')}
                            sx={{
                              border: 'none',
                              bgcolor: 'transparent',
                              p: 0,
                              m: 0,
                              cursor: 'pointer',
                              textAlign: 'right',
                              font: 'inherit',
                              color: DASHBOARD_COLORS.blue,
                              textDecoration: 'underline',
                              '&:hover': { opacity: 0.85 },
                            }}
                          >
                            {formatCount(run.rows_skipped)}
                          </Typography>
                        ) : (
                          formatCount(run.rows_skipped)
                        )}
                      </TableCell>
                      <TableCell sx={{ maxWidth: 260 }}>
                        {codes.length === 0 ? (
                          '—'
                        ) : (
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <Typography
                              component="button"
                              type="button"
                              onClick={() => void openHistoryDetail(run, 'failed')}
                              sx={{
                                border: 'none',
                                bgcolor: 'transparent',
                                p: 0,
                                m: 0,
                                cursor: 'pointer',
                                textAlign: 'left',
                                font: 'inherit',
                                color: DASHBOARD_COLORS.red,
                                textDecoration: 'underline',
                                '&:hover': { opacity: 0.85 },
                              }}
                            >
                              {codes.length <= 3
                                ? codes.join(', ')
                                : `${codes.slice(0, 3).join(', ')} +${codes.length - 3}`}
                            </Typography>
                            <Tooltip title="Copy all failed location codes">
                              <IconButton
                                size="small"
                                aria-label="Copy failed location codes"
                                onClick={() =>
                                  void copyLocationCodes(codes, {
                                    successMessage: `Copied ${codes.length} failed location code${
                                      codes.length === 1 ? '' : 's'
                                    } — paste into LocalCodes`,
                                  })
                                }
                                sx={{ color: '#666', p: 0.35 }}
                              >
                                <ContentCopyIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        )}
                      </TableCell>
                      <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                        <CsvDownloadButton
                          run={run}
                          kind={ARTIFACT_KIND.PRICE_CHANGES}
                          unavailableIds={unavailableIds}
                          downloadingKey={downloadingKey}
                          onDownload={handleDownload}
                          compact
                        />
                      </TableCell>
                      <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                        <CsvDownloadButton
                          run={run}
                          kind={ARTIFACT_KIND.API_DATA}
                          unavailableIds={unavailableIds}
                          downloadingKey={downloadingKey}
                          onDownload={handleDownload}
                          compact
                        />
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        {runs.length === 0 && !historyLoading && (
          <Box sx={{ p: 3, textAlign: 'center', color: '#999' }}>
            <Typography>No runs in the last 30 days.</Typography>
          </Box>
        )}
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_event, nextPage) => setPage(nextPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10))
            setPage(0)
          }}
          rowsPerPageOptions={[10, 25, 50]}
          showFirstButton
          showLastButton
        />
      </Card>

      <Dialog
        open={Boolean(drawer)}
        onClose={closeDrawer}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            boxShadow: '0 8px 40px rgba(0,0,0,0.18)',
            maxHeight: '80vh',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1.5,
            px: 2.75,
            py: 2.25,
            borderBottom: `1px solid ${DASHBOARD_COLORS.border}`,
            fontSize: '15px',
            fontWeight: 700,
          }}
        >
          <Box component="span" sx={{ flex: 1, minWidth: 0 }}>
            {drawer?.label || 'Items'}
          </Box>
          <Button
            size="small"
            variant="outlined"
            startIcon={
              drawerDownloading ? (
                <CircularProgress size={14} color="inherit" />
              ) : (
                <DownloadIcon fontSize="small" />
              )
            }
            disabled={drawerDownloading || drawerLoading || drawerTotal <= 0}
            onClick={() => void handleDrawerCsvDownload()}
            sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
          >
            Download CSV
          </Button>
          <IconButton
            onClick={closeDrawer}
            aria-label="Close"
            size="small"
            sx={{
              border: '1px solid rgba(0,0,0,0.13)',
              borderRadius: '8px',
              bgcolor: DASHBOARD_COLORS.bg,
              color: '#555550',
              '&:hover': { bgcolor: '#fff1f3', color: DASHBOARD_COLORS.red },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 2.75, py: 2.25 }}>
          {drawer?.searchable && (
            <Stack direction="row" sx={{ mb: 2 }}>
              <TextField
                size="small"
                fullWidth
                placeholder="Search location in this list…"
                value={drawerSearchInput}
                onChange={(event) => setDrawerSearchInput(event.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" sx={{ color: '#888882' }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Stack>
          )}
          {drawerLoading && drawerItems.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={32} />
            </Box>
          ) : drawerError ? (
            <Alert
              severity="error"
              action={
                <Typography
                  component="button"
                  type="button"
                  onClick={() => void loadDrawerItems()}
                  sx={{
                    cursor: 'pointer',
                    border: 'none',
                    bgcolor: 'transparent',
                    font: 'inherit',
                    fontWeight: 600,
                    textDecoration: 'underline',
                  }}
                >
                  Retry
                </Typography>
              }
            >
              {drawerError}
            </Alert>
          ) : (
            <>
              <Table
                size="small"
                sx={{ '& tbody tr:hover td': { bgcolor: DASHBOARD_COLORS.bg } }}
              >
                <TableHead>
                  <TableRow>
                    {(drawer?.itemType === 'location_processed' ||
                      drawer?.itemType === 'location_failed') && (
                      <>
                        <TableCell sx={detailTableHeadSx}>Location</TableCell>
                        <TableCell sx={detailTableHeadSx} align="right">
                          Location ID
                        </TableCell>
                        {drawer?.itemType === 'location_processed' && (
                          <>
                            <TableCell sx={detailTableHeadSx} align="right">
                              Updated
                            </TableCell>
                            <TableCell sx={detailTableHeadSx} align="right">
                              Inserted
                            </TableCell>
                            <TableCell sx={detailTableHeadSx} align="right">
                              Skipped
                            </TableCell>
                            <TableCell sx={detailTableHeadSx} align="right">
                              <Tooltip title={COL.rateLimitHitsHint}>
                                <span>{COL.rateLimitHits}</span>
                              </Tooltip>
                            </TableCell>
                            <TableCell sx={detailTableHeadSx} align="right">
                              <Tooltip title={COL.requestTimeoutsHint}>
                                <span>{COL.requestTimeouts}</span>
                              </Tooltip>
                            </TableCell>
                            <TableCell sx={detailTableHeadSx} align="right">
                              <Tooltip title={COL.retryPassesHint}>
                                <span>{COL.retryPasses}</span>
                              </Tooltip>
                            </TableCell>
                            <TableCell sx={detailTableHeadSx}>Completed at</TableCell>
                          </>
                        )}
                        {drawer?.itemType === 'location_failed' && (
                          <>
                            <TableCell sx={detailTableHeadSx}>Reason</TableCell>
                            <TableCell sx={detailTableHeadSx} align="right">
                              <Tooltip title={COL.rateLimitHitsHint}>
                                <span>{COL.rateLimitHits}</span>
                              </Tooltip>
                            </TableCell>
                            <TableCell sx={detailTableHeadSx} align="right">
                              <Tooltip title={COL.requestTimeoutsHint}>
                                <span>{COL.requestTimeouts}</span>
                              </Tooltip>
                            </TableCell>
                            <TableCell sx={detailTableHeadSx} align="right">
                              <Tooltip title={COL.retryPassesHint}>
                                <span>{COL.retryPasses}</span>
                              </Tooltip>
                            </TableCell>
                            <TableCell sx={detailTableHeadSx}>
                              <Tooltip title={COL.failureReasonsHint}>
                                <span>{COL.failureReasons}</span>
                              </Tooltip>
                            </TableCell>
                            <TableCell sx={detailTableHeadSx}>Failed at</TableCell>
                          </>
                        )}
                      </>
                    )}
                    {(drawer?.itemType === 'updated' ||
                      drawer?.itemType === 'inserted' ||
                      drawer?.itemType === 'skipped') && (
                      <>
                        <TableCell sx={detailTableHeadSx}>Location</TableCell>
                        <TableCell sx={detailTableHeadSx}>SKU</TableCell>
                        <TableCell sx={detailTableHeadSx}>Product</TableCell>
                        <TableCell sx={detailTableHeadSx} align="right">
                          Qty
                        </TableCell>
                        {drawer?.itemType === 'updated' && (
                          <>
                            <TableCell sx={detailTableHeadSx} align="right">
                              Old cost
                            </TableCell>
                            <TableCell sx={detailTableHeadSx} align="right">
                              New cost
                            </TableCell>
                            <TableCell sx={detailTableHeadSx} align="right">
                              Old MRP
                            </TableCell>
                            <TableCell sx={detailTableHeadSx} align="right">
                              New MRP
                            </TableCell>
                          </>
                        )}
                        {drawer?.itemType === 'inserted' && (
                          <>
                            <TableCell sx={detailTableHeadSx} align="right">
                              Cost
                            </TableCell>
                            <TableCell sx={detailTableHeadSx} align="right">
                              MRP
                            </TableCell>
                          </>
                        )}
                        {drawer?.itemType === 'skipped' && (
                          <TableCell sx={detailTableHeadSx}>Skip reason</TableCell>
                        )}
                      </>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {drawerItems.map((item) => (
                    <TableRow key={item.id}>
                      {drawer?.itemType === 'location_processed' && (
                        <>
                          <TableCell sx={detailTableCellSx}>
                            {item.location_code || '—'}
                          </TableCell>
                          <TableCell sx={detailTableCellSx} align="right">
                            {item.location_id != null ? formatCount(item.location_id) : '—'}
                          </TableCell>
                          <TableCell sx={detailTableCellSx} align="right">
                            {formatCount(item.rows_updated)}
                          </TableCell>
                          <TableCell sx={detailTableCellSx} align="right">
                            {formatCount(item.rows_inserted)}
                          </TableCell>
                          <TableCell sx={detailTableCellSx} align="right">
                            {formatCount(item.rows_skipped)}
                          </TableCell>
                          <TableCell sx={detailTableCellSx} align="right">
                            {formatCount(item.http_429_count)}
                          </TableCell>
                          <TableCell sx={detailTableCellSx} align="right">
                            {formatCount(item.timeout_count)}
                          </TableCell>
                          <TableCell sx={detailTableCellSx} align="right">
                            {formatCount(item.retry_pass_count)}
                          </TableCell>
                          <TableCell sx={detailTableCellSx}>
                            {formatDate(item.updated_at || item.created_at)}
                          </TableCell>
                        </>
                      )}
                      {drawer?.itemType === 'location_failed' && (
                        <>
                          <TableCell sx={detailTableCellSx}>
                            {item.location_code || '—'}
                          </TableCell>
                          <TableCell sx={detailTableCellSx} align="right">
                            {item.location_id != null ? formatCount(item.location_id) : '—'}
                          </TableCell>
                          <TableCell sx={detailTableCellSx}>
                            <AdminReasonText
                              raw={item.failure_reason || item.last_reason}
                            />
                          </TableCell>
                          <TableCell sx={detailTableCellSx} align="right">
                            {formatCount(item.http_429_count)}
                          </TableCell>
                          <TableCell sx={detailTableCellSx} align="right">
                            {formatCount(item.timeout_count)}
                          </TableCell>
                          <TableCell sx={detailTableCellSx} align="right">
                            {formatCount(item.retry_pass_count)}
                          </TableCell>
                          <TableCell sx={detailTableCellSx}>
                            <AdminReasonText raw={item.reasons_seen} tally />
                          </TableCell>
                          <TableCell sx={detailTableCellSx}>
                            {formatDate(
                              item.last_failed_at || item.updated_at || item.created_at,
                            )}
                          </TableCell>
                        </>
                      )}
                      {drawer?.itemType === 'updated' && (
                        <>
                          <TableCell sx={detailTableCellSx}>
                            {item.location_code || '—'}
                          </TableCell>
                          <TableCell sx={detailTableCellSx}>{item.sku || '—'}</TableCell>
                          <TableCell sx={detailTableCellSx}>
                            {item.product_name || '—'}
                          </TableCell>
                          <TableCell sx={detailTableCellSx} align="right">
                            {formatCount(item.quantity)}
                          </TableCell>
                          <TableCell sx={detailTableCellSx} align="right">
                            {formatMoney(item.unit_cost_before)}
                          </TableCell>
                          <TableCell sx={detailTableCellSx} align="right">
                            {formatMoney(item.unit_cost_after)}
                          </TableCell>
                          <TableCell sx={detailTableCellSx} align="right">
                            {formatMoney(item.unit_price_before)}
                          </TableCell>
                          <TableCell sx={detailTableCellSx} align="right">
                            {formatMoney(item.unit_price_after)}
                          </TableCell>
                        </>
                      )}
                      {drawer?.itemType === 'inserted' && (
                        <>
                          <TableCell sx={detailTableCellSx}>
                            {item.location_code || '—'}
                          </TableCell>
                          <TableCell sx={detailTableCellSx}>{item.sku || '—'}</TableCell>
                          <TableCell sx={detailTableCellSx}>
                            {item.product_name || '—'}
                          </TableCell>
                          <TableCell sx={detailTableCellSx} align="right">
                            {formatCount(item.quantity)}
                          </TableCell>
                          <TableCell sx={detailTableCellSx} align="right">
                            {formatMoney(item.unit_cost_after)}
                          </TableCell>
                          <TableCell sx={detailTableCellSx} align="right">
                            {formatMoney(item.unit_price_after)}
                          </TableCell>
                        </>
                      )}
                      {drawer?.itemType === 'skipped' && (
                        <>
                          <TableCell sx={detailTableCellSx}>
                            {item.location_code || '—'}
                          </TableCell>
                          <TableCell sx={detailTableCellSx}>{item.sku || '—'}</TableCell>
                          <TableCell sx={detailTableCellSx}>
                            {item.product_name || '—'}
                          </TableCell>
                          <TableCell sx={detailTableCellSx} align="right">
                            {formatCount(item.quantity)}
                          </TableCell>
                          <TableCell sx={detailTableCellSx}>
                            {item.skip_reason || '—'}
                          </TableCell>
                        </>
                      )}
                    </TableRow>
                  ))}
                  {drawerItems.length === 0 && (
                    <TableRow>
                      <TableCell
                        colSpan={8}
                        sx={{ ...detailTableCellSx, borderBottom: 'none' }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          {latestRunning
                            ? 'Waiting for first flush…'
                            : 'No records found.'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              <ReportTablePagination
                compact
                pagination={{
                  total: drawerTotal,
                  page: drawerPage,
                  limit: drawerRowsPerPage,
                }}
                onPageChange={setDrawerPage}
                onRowsPerPageChange={(nextRows) => {
                  setDrawerRowsPerPage(nextRows)
                  setDrawerPage(1)
                }}
                rowsPerPageOptions={DRAWER_ROWS_OPTIONS}
              />
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(historyLocations)}
        onClose={closeHistoryLocations}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            boxShadow: '0 8px 40px rgba(0,0,0,0.18)',
            maxHeight: '80vh',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1.5,
            px: 2.75,
            py: 2.25,
            borderBottom: `1px solid ${DASHBOARD_COLORS.border}`,
            fontSize: '15px',
            fontWeight: 700,
          }}
        >
          <Box component="span" sx={{ flex: 1, minWidth: 0 }}>
            {historyLocations?.label || 'Locations'}
          </Box>
          <Button
            size="small"
            variant="outlined"
            startIcon={<ContentCopyIcon fontSize="small" />}
            disabled={
              historyLocationsLoading ||
              !(historyLocations?.codes || []).length
            }
            onClick={() =>
              void copyLocationCodes(historyLocations?.codes, {
                successMessage:
                  historyLocations?.kind === 'failed'
                    ? `Copied ${(historyLocations?.codes || []).length} failed location code${
                        (historyLocations?.codes || []).length === 1 ? '' : 's'
                      } — paste into LocalCodes`
                    : undefined,
              })
            }
            sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
          >
            Copy codes
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={
              historyLocationsDownloading ? (
                <CircularProgress size={14} color="inherit" />
              ) : (
                <DownloadIcon fontSize="small" />
              )
            }
            disabled={
              historyLocationsDownloading ||
              historyLocationsLoading ||
              historyLocationsTotal <= 0
            }
            onClick={() => void handleHistoryLocationsCsvDownload()}
            sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
          >
            Download CSV
          </Button>
          <IconButton
            onClick={closeHistoryLocations}
            aria-label="Close"
            size="small"
            sx={{
              border: '1px solid rgba(0,0,0,0.13)',
              borderRadius: '8px',
              bgcolor: DASHBOARD_COLORS.bg,
              color: '#555550',
              '&:hover': { bgcolor: '#fff1f3', color: DASHBOARD_COLORS.red },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 2.75, py: 2.25 }}>
          <Stack direction="row" sx={{ mb: 2 }}>
            <TextField
              size="small"
              fullWidth
              placeholder="Search by code or name…"
              value={historyLocationsSearchInput}
              onChange={(event) => setHistoryLocationsSearchInput(event.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" sx={{ color: '#888882' }} />
                  </InputAdornment>
                ),
              }}
            />
          </Stack>
          {historyLocationsLoading && historyLocationItems.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={32} />
            </Box>
          ) : historyLocationsError ? (
            <Alert
              severity="error"
              action={
                <Typography
                  component="button"
                  type="button"
                  onClick={() => void loadHistoryLocations()}
                  sx={{
                    cursor: 'pointer',
                    border: 'none',
                    bgcolor: 'transparent',
                    font: 'inherit',
                    fontWeight: 600,
                    textDecoration: 'underline',
                  }}
                >
                  Retry
                </Typography>
              }
            >
              {historyLocationsError}
            </Alert>
          ) : (
            <>
              <Table
                size="small"
                sx={{ '& tbody tr:hover td': { bgcolor: DASHBOARD_COLORS.bg } }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell sx={detailTableHeadSx}>Location code</TableCell>
                    <TableCell sx={detailTableHeadSx}>Location name</TableCell>
                    {historyLocations?.kind === 'failed' && (
                      <>
                        <TableCell sx={detailTableHeadSx}>Reason</TableCell>
                        <TableCell sx={detailTableHeadSx}>Failed at</TableCell>
                      </>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {historyLocationItems.map((item, idx) => (
                    <TableRow key={`${item.location_code || 'row'}-${idx}`}>
                      <TableCell sx={detailTableCellSx}>
                        {item.location_code || '—'}
                      </TableCell>
                      <TableCell sx={detailTableCellSx}>
                        {item.location_name || '—'}
                      </TableCell>
                      {historyLocations?.kind === 'failed' && (
                        <>
                          <TableCell sx={detailTableCellSx}>
                            <AdminReasonText raw={item.reason} />
                          </TableCell>
                          <TableCell sx={detailTableCellSx}>
                            {item.failed_at ? formatDate(item.failed_at) : '—'}
                          </TableCell>
                        </>
                      )}
                    </TableRow>
                  ))}
                  {historyLocationItems.length === 0 && (
                    <TableRow>
                      <TableCell
                        colSpan={historyLocations?.kind === 'failed' ? 4 : 2}
                        sx={{ ...detailTableCellSx, borderBottom: 'none' }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          {historyLocations?.kind === 'processed' &&
                          (historyLocations?.expectedCount || 0) > 0
                            ? 'No processed location codes stored for this run yet.'
                            : 'No records found.'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              <ReportTablePagination
                compact
                pagination={{
                  total: historyLocationsTotal,
                  page: historyLocationsPage,
                  limit: historyLocationsRows,
                }}
                onPageChange={setHistoryLocationsPage}
                onRowsPerPageChange={(nextRows) => {
                  setHistoryLocationsRows(nextRows)
                  setHistoryLocationsPage(1)
                }}
                rowsPerPageOptions={DRAWER_ROWS_OPTIONS}
              />
            </>
          )}
        </DialogContent>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  )
}

export default StockSyncAdminPage
